/**
 * Header chrome interactions: predictive search panel, mobile menu, and
 * scroll-past-hero behavior for image heroes. Vanilla, no dependencies.
 * Mood/palette switching is handled by the inline script in ppd-theme-system.liquid.
 */
(() => {
  const root = document.body;
  if (!root || !root.classList.contains('ppd-theme')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ *
   * Predictive search panel
   * ------------------------------------------------------------------ */
  const searchToggle = document.querySelector('.ppd-header-search-toggle');
  const searchPanel = document.querySelector('.ppd-header-search-panel');
  const searchInput = searchPanel?.querySelector('input[type="search"]');
  const searchResults = searchPanel?.querySelector('.ppd-header-search-results');
  const searchClose = searchPanel?.querySelector('.ppd-header-search-close');

  let searchOpen = false;
  let debounceId = null;
  let lastQuery = '';
  let lastRequest = 0;
  let searchCloseTimer = null;
  let activeOption = -1;
  const SEARCH_CLOSE_MS = 240;

  /* The results render as listbox options; this keeps the combobox state
     (aria-expanded / aria-activedescendant) in sync when the list clears. */
  const clearSearchState = () => {
    activeOption = -1;
    searchInput?.removeAttribute('aria-activedescendant');
    searchInput?.setAttribute('aria-expanded', 'false');
  };

  const optionEls = () => (searchResults ? [...searchResults.querySelectorAll('[role="option"]')] : []);

  const setActiveOption = (index, { refocusInput = true } = {}) => {
    const els = optionEls();
    if (!els.length) return;
    const clamped = ((index % els.length) + els.length) % els.length;
    els.forEach((el, i) => el.setAttribute('aria-selected', String(i === clamped)));
    activeOption = clamped;
    const active = els[clamped];
    /* aria-activedescendant only applies while focus stays in the combobox;
       when focus moves onto an option itself, selection alone announces it. */
    if (refocusInput) {
      searchInput?.setAttribute('aria-activedescendant', active.id);
      searchInput?.focus({ preventScroll: true });
    } else {
      searchInput?.removeAttribute('aria-activedescendant');
    }
    searchInput?.setAttribute('aria-expanded', 'true');
    active.scrollIntoView({ block: 'nearest' });
  };

  const finishSearchClose = () => {
    if (!searchPanel) return;
    searchCloseTimer = null;
    searchPanel.hidden = true;
    searchPanel.removeAttribute('data-closing');
    searchPanel.removeAttribute('data-open');
    searchPanel.removeAttribute('inert');
    searchOpen = false;
  };

  const setSearchOpen = (open) => {
    if (!searchPanel || !searchToggle) return;
    if (open) {
      if (searchCloseTimer) {
        clearTimeout(searchCloseTimer);
        searchCloseTimer = null;
      }
      searchPanel.hidden = false;
      searchPanel.removeAttribute('data-closing');
      searchPanel.removeAttribute('inert');
      /* Force a style recalc between unhiding and arming the open state so
         the slide/fade transition runs instead of snapping to the end. */
      void searchPanel.offsetWidth;
      searchPanel.setAttribute('data-open', 'true');
      searchOpen = true;
      searchToggle.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', onSearchOutside, true);
      /* Commit the open state before moving focus: visibility flips with the
         data-open state (it is deliberately not transitioned — a
         transitioned visibility still computes hidden at 0% progress and a
         focus() call then silently fails, stranding focus on the toggle). */
      void searchPanel.offsetWidth;
      searchInput?.focus({ preventScroll: true });
      return;
    }
    if (searchPanel.hidden && !searchPanel.hasAttribute('data-open')) return;
    const wasInside = searchPanel.contains(document.activeElement);
    searchPanel.removeAttribute('data-open');
    searchPanel.setAttribute('data-closing', 'true');
    /* Keep Tab out of the fading panel; focus already moved below. */
    searchPanel.setAttribute('inert', '');
    searchToggle.setAttribute('aria-expanded', 'false');
    clearSearchState();
    document.removeEventListener('click', onSearchOutside, true);
    if (wasInside) searchToggle.focus();
    if (reduceMotion) {
      finishSearchClose();
      return;
    }
    if (searchCloseTimer) clearTimeout(searchCloseTimer);
    searchCloseTimer = setTimeout(finishSearchClose, SEARCH_CLOSE_MS);
  };

  const onSearchOutside = (event) => {
    if (!searchPanel.contains(event.target) && !searchToggle.contains(event.target)) {
      setSearchOpen(false);
    }
  };

  const renderEmpty = (query) => {
    const safe = (query || '').replace(/["&<>]/g, (c) => ({ '"': '&quot;', '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    searchResults.innerHTML = `<p class="ppd-header-search-empty">${window.ppdHeaderStrings?.noResults?.replace('{query}', safe) || 'No products match.'}</p>`;
    clearSearchState();
  };

  const money = (value) => {
    if (value == null) return '';
    // The suggest API returns decimal dollars ("14.00"); integer values are
    // cents. Detect via the raw text since Number("14.00") === 14.
    const text = String(value).trim();
    const amount = Number(text);
    if (Number.isNaN(amount)) return text;
    const dollars = text.includes('.') ? amount : amount / 100;
    try {
      return new Intl.NumberFormat(window.Shopify?.locale || document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(dollars);
    } catch {
      return `$${dollars.toFixed(2)}`;
    }
  };

  const renderResults = (products) => {
    if (!products.length) {
      renderEmpty(lastQuery);
      return;
    }
    const listId = searchResults.id || 'ppd-header-search-results';
    searchResults.innerHTML = products
      .map((product, index) => {
        const img = product.featured_image?.url || product.image || '';
        const alt = (product.title || '').replace(/"/g, '&quot;');
        const price = product.price != null ? money(product.price) : '';
        return `<a class="ppd-header-search-result" id="${listId}-opt-${index}" role="option" aria-selected="false" href="${product.url}">
          ${img ? `<img src="${img}" alt="${alt}" loading="lazy">` : '<span aria-hidden="true"></span>'}
          <span><strong>${product.title || ''}</strong></span>
          <b>${price}</b>
        </a>`;
      })
      .join('');
    activeOption = -1;
    searchInput?.removeAttribute('aria-activedescendant');
    searchInput?.setAttribute('aria-expanded', 'true');
  };

  const fetchResults = async (query) => {
    lastQuery = query;
    if (!query.trim()) {
      searchResults.innerHTML = '';
      clearSearchState();
      return;
    }
    const requestId = ++lastRequest;
    const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?resources[type]=product&resources[limit]=5&q=${encodeURIComponent(query)}`;
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (lastRequest !== requestId) return;
      if (!res.ok) throw new Error('search failed');
      const data = await res.json();
      const products = data?.resources?.results?.products || [];
      renderResults(products);
    } catch {
      if (lastRequest === requestId) renderEmpty(query);
    }
  };

  const onSearchInput = (event) => {
    const query = event.currentTarget.value;
    if (debounceId) clearTimeout(debounceId);
    debounceId = setTimeout(() => fetchResults(query), 200);
  };

  searchToggle?.addEventListener('click', () => {
    if (!searchPanel) return;
    /* During the close animation the panel is still unhidden, so read intent
       from visibility state: a click mid-close reopens instead of stalling. */
    setSearchOpen(searchPanel.hidden || searchPanel.hasAttribute('data-closing'));
  });
  searchClose?.addEventListener('click', () => setSearchOpen(false));
  searchInput?.addEventListener('input', onSearchInput);
  searchInput?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setSearchOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!optionEls().length) return;
      event.preventDefault();
      /* Focus stays in the combobox; the active option is exposed through
         aria-activedescendant so typing can continue uninterrupted. */
      setActiveOption(activeOption + (event.key === 'ArrowDown' ? 1 : -1));
      return;
    }
    if (event.key === 'Enter' && activeOption >= 0) {
      const target = optionEls()[activeOption];
      if (target?.getAttribute('href')) {
        event.preventDefault();
        window.location.assign(target.getAttribute('href'));
      }
    }
  });
  /* Arrow-key nav for keyboard users who Tab into the results themselves. */
  searchResults?.addEventListener('keydown', (event) => {
    const current = event.target?.closest?.('[role="option"]');
    if (!current) return;
    const els = optionEls();
    const at = els.indexOf(current);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveOption(at + (event.key === 'ArrowDown' ? 1 : -1), { refocusInput: false });
      els[activeOption]?.focus({ preventScroll: true });
    } else if (event.key === 'Escape') {
      setSearchOpen(false);
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && searchOpen) setSearchOpen(false);
  });

  /* The PPD header owns its own bag markup instead of Horizon's cart-icon
     element, so it must subscribe to Shopify's standard cart event directly.
     Without this bridge the item reaches the cart, but the visible badge and
     accessible count stay at the server-rendered value until navigation. */
  const bagLink = document.querySelector('.ppd-bag-link');
  const bagCount = document.querySelector('[data-ppd-bag-count]');
  const updatePpdBagCount = (count) => {
    if (!bagLink || !bagCount || !Number.isFinite(count)) return;
    const normalizedCount = Math.max(0, Math.floor(count));
    bagCount.textContent = String(normalizedCount);
    const template = window.ppdHeaderStrings?.cartCount;
    if (template) bagLink.setAttribute('aria-label', template.replace('__COUNT__', String(normalizedCount)));
  };

  document.addEventListener('shopify:cart:lines-update', (event) => {
    event.promise?.then(({ cart, detail }) => {
      const itemCount = cart?.totalQuantity ?? detail?.itemCount;
      if (itemCount !== undefined) updatePpdBagCount(itemCount);
    }).catch(() => {});
  });

  /* ------------------------------------------------------------------ *
   * Mobile menu
   * ------------------------------------------------------------------ */
  const mobileNavigation = document.querySelector('.ppd-mobile-navigation');
  const menuButton = document.querySelector('.ppd-menu-button');
  const mobilePanel = document.querySelector('.ppd-mobile-panel');
  let menuTrigger = null;

  const syncMenuState = (open, { moveFocus = true } = {}) => {
    if (!mobileNavigation || !menuButton || !mobilePanel) return;
    /* Capture before the inert toggle below moves focus out of the panel. */
    const focusInside = !open && mobilePanel.contains(document.activeElement);
    menuButton.setAttribute('aria-expanded', String(open));
    mobilePanel.setAttribute('aria-hidden', String(!open));
    mobilePanel.setAttribute('data-open', String(open));
    mobilePanel.toggleAttribute('inert', !open);
    root.classList.toggle('ppd-menu-open', open);
    if (open && moveFocus) {
      /* The native summary remains the close control. Move into the first
         destination so opening the drawer begins the navigation task. */
      const firstTarget = mobilePanel.querySelector('a[href], button:not([disabled])');
      firstTarget?.focus({ preventScroll: true });
      return;
    }
    /* Return focus to the element that opened the drawer. */
    if (!open) {
      if (focusInside) (menuTrigger || menuButton)?.focus?.({ preventScroll: true });
      menuTrigger = null;
    }
  };

  const setMenuOpen = (open, trigger) => {
    if (!mobileNavigation) return;
    if (open) menuTrigger = trigger || document.activeElement;
    mobileNavigation.open = open;
    syncMenuState(open);
  };

  mobileNavigation?.addEventListener('toggle', () => {
    if (mobileNavigation.open && !menuTrigger) menuTrigger = menuButton;
    syncMenuState(mobileNavigation.open);
  });

  /* Only JavaScript applies inert. Without it, the native details/summary
     pair remains a complete, keyboard-operable mobile navigation. */
  syncMenuState(false, { moveFocus: false });

  mobilePanel?.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => setMenuOpen(false));
  });

  document.addEventListener('keydown', (event) => {
    if (menuButton?.getAttribute('aria-expanded') !== 'true') return;
    if (event.key === 'Escape') {
      setMenuOpen(false);
      return;
    }
    /* Trap Tab inside the open drawer. */
    if (event.key !== 'Tab' || !mobilePanel) return;
    const items = [menuButton, ...mobilePanel.querySelectorAll('a[href], button:not([disabled])')].filter(
      (el) => el.getClientRects().length > 0
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------------------------ *
   * Scroll-past-hero (image hero only)
   * ------------------------------------------------------------------ */
  // Only relevant on the home page when a full-image hero is present.
  const fullImageHero = document.querySelector('.ppd-hero--full-image');
  if (fullImageHero) {
    root.dataset.ppdHomeHero = 'image';
  }

  const header = document.querySelector('.ppd-site-header');
  let scrollHandler = null;

  const wireHeroScroll = () => {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
    if (!header || !fullImageHero || root.dataset.ppdHomeHero !== 'image') {
      header?.classList.remove('is-past-hero');
      return;
    }
    scrollHandler = () => {
      const heroEnd = fullImageHero.offsetTop + fullImageHero.offsetHeight - header.offsetHeight;
      header.classList.toggle('is-past-hero', window.scrollY >= heroEnd);
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });
    scrollHandler();
  };

  wireHeroScroll();

  // Re-evaluate after fonts/images settle to get accurate hero height.
  window.addEventListener('load', wireHeroScroll, { once: true });
})();
